import type { GovernancePolicyFormData } from '@experiences/interfaces';
import {
    useCallback,
    useMemo,
} from 'react';
import { useFormContext } from 'react-hook-form';

const useGovernancePolicyCreateDetailsStepViewModel = () => {
    const {
        setValue,
        watch,
        clearErrors,
        formState: { errors },
    } = useFormContext<GovernancePolicyFormData>();

    const name = watch('name') || '';
    const description = watch('description') || '';

    const handleNameChange = useCallback((event: CustomEvent) => {
        const newName = event.detail as string;
        setValue('name', newName, {
            shouldValidate: true,
            shouldDirty: true,
        });
        if (errors.name) {
            clearErrors('name');
        }
    }, [ setValue, errors.name, clearErrors ]);

    const handleDescriptionChange = useCallback((event: CustomEvent) => {
        const newDescription = event.detail as string;
        setValue('description', newDescription, {
            shouldValidate: true,
            shouldDirty: true,
        });
        if (errors.description) {
            clearErrors('description');
        }
    }, [ setValue, errors.description, clearErrors ]);

    // `stepHooks` is returned (not registered here) so the component can decide
    // whether to wire it into the wizard. In drawer mode the current wizard
    // step is Review — registering would clobber Review's onComplete/nextLabel
    // and break Save, so the component skips the wizard hook registration.
    const stepHooks = useMemo(() => {
        const isNameInvalid = !name || name.trim() === '';
        return { disableNext: isNameInvalid };
    }, [ name ]);

    return {
        name,
        description,
        errors,
        handleNameChange,
        handleDescriptionChange,
        stepHooks,
    };
};

export default useGovernancePolicyCreateDetailsStepViewModel;

